import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { createGlobalStyle } from "styled-components";
import { API_URL } from "src/scenes/App";
import {
    BusinessPortalHeader,
    LottieLoadingIndicator,
    PortalPageShell,
    PortalLoadingState,
    Toast
} from "src/components";
import SurveyTable from "./components/SurveyTable";
import HowItWorks from "./components/HowItWorks";
import SurveyInsightPage from "./components/SurveyInsightPage";
import SurveyForm from "./forms/SurveyForm";
import { SurveyStatusType, SortableColumns, SortableColumnState, TableType, SortablePollColumns } from "src/constants/survey";
import PollsTable from "./components/PollsTable";
import PollsForm from "./forms/PollsCreateForm";
import { getSurveyPollFeatureContext } from "./components/Utils";
import pluralize from "pluralize";
import { customNames } from "src/utils/helpers";
import { hasPermissions, POLLS, SURVEYS } from "src/constants/permissions";

const CALENDAR_REQUEST_QUERY_PARAMS = {
    SURVEY: "viewSurveyUuid",
    POLL: "viewPollUuid"
};
const CALENDAR_REQUEST_SEARCH_STATUSES = [
    SurveyStatusType.ACTIVE,
    SurveyStatusType.COMPLETE,
    SurveyStatusType.SCHEDULED
];

const getCalendarRequest = (props) => {
    const searchParams = new URLSearchParams(props.location?.search || window.location.search || "");
    const surveyUuid = props.location?.state?.viewSurveyUuid || searchParams.get(CALENDAR_REQUEST_QUERY_PARAMS.SURVEY) || "";
    if (surveyUuid) {
        return { type: TableType.Surveys, uuid: surveyUuid };
    }

    const pollUuid = props.location?.state?.viewPollUuid || searchParams.get(CALENDAR_REQUEST_QUERY_PARAMS.POLL) || "";
    if (pollUuid) {
        return { type: TableType.Polls, uuid: pollUuid };
    }

    return null;
};

const SurveyGlobalStyles = createGlobalStyle`
    input {
        text-align: center;
        font-size: 16px;
        caret-color: auto;
    }

    ::-webkit-input-placeholder {
        text-align: center;
        font-size: 16px;
    }

    :-moz-placeholder {
        text-align: center;
        font-size: 16px;
    }

    .react-datepicker-wrapper {
        width: 100% !important;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    @media screen and (max-width: 1200px) {
        .react-datepicker-wrapper {
            margin-bottom: 1rem;
        }
    }
`;

const Survey = (props) => {
    const [fetching, setFetching] = useState(false);
    const [surveyArray, setSurveyArray] = useState([]);
    const [pollsArray, setPollsArray] = useState([]);
    const [sortableColumns, setSortableColumns] = useState([]);
    const [isShowSurveyForm, setIsShowSurveyForm] = useState(false);
    const [surveyToEdit, setSurveyToEdit] = useState({});
    const [isShowHowItWorks, setIsShowHowItWorks] = useState(false);
    const [isShowInsights, setIsShowInsights] = useState(false);
    const [surveyUuid, setSurveyUuid] = useState("");
    const [initialized, setInitialized] = useState(false);
    const [isShowPolls, setIsShowPolls] = useState(false);
    const [isShowCreatePollForm, setIsShowCreatePollForm] = useState(false);
    const [pollToEdit, setPollToEdit] = useState("");
    const [selectedSurveyOrPollStatusType, setSelectedSurveyOrPollStatusType] = useState(SurveyStatusType.ACTIVE);
    const [calendarRequest, setCalendarRequest] = useState(() => getCalendarRequest(props));
    const [calendarPollResultsUuid, setCalendarPollResultsUuid] = useState("");
    const surveyControllerRef = useRef(new AbortController());
    const pollsControllerRef = useRef(new AbortController());
    const pendingRequestsRef = useRef(0);
    const featureContext = getSurveyPollFeatureContext(props?.user?.featureFlags);
    const { hasPolls, hasSurveys } = featureContext;
    const userPermissions = (() => {
        if (Array.isArray(props?.user?.permissions)) {
            return props.user.permissions;
        }

        try {
            return JSON.parse(props?.user?.permissions || "[]");
        } catch {
            return [];
        }
    })();
    const canAccessSurveys = hasSurveys && hasPermissions([SURVEYS], userPermissions);
    const canAccessPolls = hasPolls && hasPermissions([POLLS], userPermissions);
    const pageFeatureContext = {
        hasSurveys: canAccessSurveys,
        hasPolls: canAccessPolls,
        title:
            canAccessSurveys && canAccessPolls
                ? "Surveys & Polls"
                : canAccessSurveys
                    ? "Surveys"
                    : canAccessPolls
                        ? "Polls"
                        : "",
        singularLabel:
            canAccessSurveys && canAccessPolls
                ? "survey and poll"
                : canAccessSurveys
                    ? "survey"
                    : canAccessPolls
                        ? "poll"
                        : "",
        options: [
            ...(canAccessPolls ? [{ value: TableType.Polls, label: "Polls" }] : []),
            ...(canAccessSurveys ? [{ value: TableType.Surveys, label: "Surveys" }] : [])
        ]
    };

    useEffect(() => {
        safeToInit();
    }, []);

    const safeToInit = () => {
        if (calendarRequest?.type === TableType.Surveys && !canAccessSurveys) {
            Toast.error("You do not have access to Surveys.");
            clearCalendarRequest();
            setCalendarRequest(null);
        }

        if (calendarRequest?.type === TableType.Polls && !canAccessPolls) {
            Toast.error("You do not have access to Polls.");
            clearCalendarRequest();
            setCalendarRequest(null);
        }

        if (canAccessSurveys) {
            if (calendarRequest?.type === TableType.Surveys) {
                setIsShowPolls(false);
            }

            getSurveys(SurveyStatusType.ACTIVE);
        }

        if (canAccessPolls) {
            if (calendarRequest?.type === TableType.Polls) {
                setIsShowPolls(true);
            }

            getPolls(SurveyStatusType.ACTIVE);
        }

        if (!canAccessSurveys && !canAccessPolls) {
            setInitialized(true);
        }
    };

    const clearCalendarRequest = () => {
        if (props.history?.replace) {
            props.history.replace("/survey");
            return;
        }

        window.history.replaceState({}, "", "/survey");
    };

    const startFetching = () => {
        pendingRequestsRef.current += 1;
        setFetching(true);
    };

    const finishFetching = () => {
        pendingRequestsRef.current = Math.max(pendingRequestsRef.current - 1, 0);
        setFetching(pendingRequestsRef.current > 0);
    };

    const getSurveysUrl = (surveyType) => {
        let URL = `${API_URL}/survey/company/allSurveys`;
        if (surveyType) {
            URL += `?filter=${surveyType}`;
        }
        return URL;
    };

    const fetchSurveys = (surveyType, signal) => axios.get(getSurveysUrl(surveyType), {
        headers: {
            Authorization: `Bearer ${props.user.token}`
        },
        signal
    }).then(res => res.data);

    const openSurveyFromCalendarRequest = (survey, surveyType, surveys) => {
        setSelectedSurveyOrPollStatusType(surveyType);
        setSurveyArray(surveys);
        setIsShowPolls(false);
        if (surveyType !== SurveyStatusType.SCHEDULED) {
            surveyInsights(survey.uuid);
        }
        clearCalendarRequest();
        setCalendarRequest(null);
    };

    const openCalendarSurveyRequest = async (request, loadedSurveys, loadedSurveyType) => {
        const loadedSurvey = loadedSurveys.find(survey => survey.uuid === request.uuid);
        if (loadedSurvey) {
            openSurveyFromCalendarRequest(loadedSurvey, loadedSurveyType, loadedSurveys);
            return;
        }

        const remainingSurveyTypes = CALENDAR_REQUEST_SEARCH_STATUSES.filter(surveyType => surveyType !== loadedSurveyType);
        for (const surveyType of remainingSurveyTypes) {
            const surveys = await fetchSurveys(surveyType, surveyControllerRef.current.signal);
            const requestedSurvey = surveys.find(survey => survey.uuid === request.uuid);
            if (requestedSurvey) {
                openSurveyFromCalendarRequest(requestedSurvey, surveyType, surveys);
                return;
            }
        }

        Toast.error("The survey could not be found.");
        clearCalendarRequest();
        setCalendarRequest(null);
    };

    const getPollsUrl = (pollType) => {
        let URL = `${API_URL}/polls/companyPolls`;
        if (pollType) {
            URL += `?filter=${pollType}`;
        }
        return URL;
    };

    const fetchPolls = (pollType, signal) => axios.get(getPollsUrl(pollType), {
        headers: {
            Authorization: `Bearer ${props.user.token}`
        },
        signal
    }).then(res => res.data.polls || []);

    const openPollFromCalendarRequest = (poll, pollType, polls) => {
        setSelectedSurveyOrPollStatusType(pollType);
        setPollsArray(polls);
        setIsShowPolls(true);
        if (pollType !== SurveyStatusType.SCHEDULED) {
            setCalendarPollResultsUuid(poll.uuid);
        }
        clearCalendarRequest();
        setCalendarRequest(null);
    };

    const openCalendarPollRequest = async (request, loadedPolls, loadedPollType) => {
        const loadedPoll = loadedPolls.find(poll => poll.uuid === request.uuid);
        if (loadedPoll) {
            openPollFromCalendarRequest(loadedPoll, loadedPollType, loadedPolls);
            return;
        }

        const remainingPollTypes = CALENDAR_REQUEST_SEARCH_STATUSES.filter(pollType => pollType !== loadedPollType);
        for (const pollType of remainingPollTypes) {
            const polls = await fetchPolls(pollType, pollsControllerRef.current.signal);
            const requestedPoll = polls.find(poll => poll.uuid === request.uuid);
            if (requestedPoll) {
                openPollFromCalendarRequest(requestedPoll, pollType, polls);
                return;
            }
        }

        Toast.error("The poll could not be found.");
        clearCalendarRequest();
        setCalendarRequest(null);
    };

    const clearCalendarPollResultsUuid = () => setCalendarPollResultsUuid("");

    const getSurveys = (surveyType) => {
        if (!canAccessSurveys) {
            return;
        }

        if (surveyControllerRef.current) {
            surveyControllerRef.current.abort();
        }

        surveyControllerRef.current = new AbortController();
        startFetching();
        fetchSurveys(surveyType, surveyControllerRef.current.signal).then(async surveys => {
            setSurveyArray(surveys);
            setSortableColumns(prevColumns => ({
                ...prevColumns,
                [SortableColumns.SURVEY_NAME]: SortableColumnState.ASCENDING
            }));
            if (calendarRequest?.type === TableType.Surveys) {
                await openCalendarSurveyRequest(calendarRequest, surveys, surveyType);
            }
            if (!initialized) {
                setInitialized(true);
            }
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.error(error.getApiErrorDetails().message);
            }
        }).finally(() => {
            finishFetching();
        });
    };

    const getPolls = (pollType) => {
        if (!canAccessPolls) {
            return;
        }

        if (pollsControllerRef.current) {
            pollsControllerRef.current.abort();
        }

        pollsControllerRef.current = new AbortController();
        startFetching();
        fetchPolls(pollType, pollsControllerRef.current.signal).then(async polls => {
            setPollsArray(polls);
            setSortableColumns(prevColumns => ({
                ...prevColumns,
                [SortablePollColumns.POLL_QUESTION]: SortableColumnState.ASCENDING
            }));
            if (calendarRequest?.type === TableType.Polls) {
                await openCalendarPollRequest(calendarRequest, polls, pollType);
            }
            setInitialized(true);
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.error(error.getApiErrorDetails().message);
            }
        }).finally(() => {
            finishFetching();
        });
    };

    const showCreatePollForm = (isShow) => {
        setPollToEdit("");
        setIsShowCreatePollForm(isShow);
    };

    const showEditPollForm = (pollUuid) => {
        setPollToEdit(pollUuid);
        setIsShowCreatePollForm(true);
    };

    const showSurveyForm = (isShow) => {
        setSurveyToEdit("");
        setIsShowSurveyForm(isShow);
    };

    const showEditSurveyForm = (surveyUuid) => {
        setSurveyToEdit(surveyUuid);
        setIsShowSurveyForm(true);
    };

    const showHowItWorks = (isShow) => setIsShowHowItWorks(isShow);
    const surveyInsights = (uuid) => {
        setSurveyUuid(uuid);
        setIsShowInsights(true);
    };
    const showInsights = (isShow) => setIsShowInsights(isShow);
    const setFetchingStatus = (status) => setFetching(status);

    const sortField = (sortableState, fieldName) => {
        const arrayToSort = isShowPolls ? pollsArray : surveyArray;
        const sortedArray = [...arrayToSort].sort((a, b) => (
            sortableState === SortableColumnState.ASCENDING
                ? a[fieldName] > b[fieldName] ? 1 : -1
                : a[fieldName] < b[fieldName] ? 1 : -1
        ));
        if (isShowPolls) {
            setPollsArray(sortedArray);
        } else {
            setSurveyArray(sortedArray);
        }
    };

    const sortColumns = (columnIndex) => {
        const newSortableColumns = { ...sortableColumns };
        let fieldName = "";

        Object.keys(newSortableColumns).forEach((_, i) => {
            newSortableColumns[i] = i === columnIndex
                ? (newSortableColumns[columnIndex] === SortableColumnState.ASCENDING
                    ? SortableColumnState.DESCENDING
                    : SortableColumnState.ASCENDING)
                : SortableColumnState.INACTIVE;
        });

        if (isShowPolls) {
            fieldName = getFieldNameForPollColumn(columnIndex);
        } else {
            fieldName = getFieldNameForSurveyColumn(columnIndex);
        }

        setSortableColumns(newSortableColumns);

        if (fieldName) {
            sortField(newSortableColumns[columnIndex], fieldName);
        }
    };

    const getFieldNameForPollColumn = (columnIndex) => {
        switch (columnIndex) {
            case SortablePollColumns.POLL_QUESTION: return "question";
            case SortablePollColumns.POLL_VISIBILITY: return "pollVisibility";
            case SortablePollColumns.NUMBER_OF_OPTIONS: return "numberOfOptions";
            case SortablePollColumns.START_DATE: return "startDate";
            case SortablePollColumns.FINISH_DATE: return "endDate";
            case SortablePollColumns.STATUS: return "status";
            default: return "";
        }
    };

    const getFieldNameForSurveyColumn = (columnIndex) => {
        switch (columnIndex) {
            case SortableColumns.SURVEY_NAME: return "name";
            case SortableColumns.SURVEY_TYPE: return "surveyType";
            case SortableColumns.USER_GROUP: return "userGroup";
            case SortableColumns.START_DATE: return "startDate";
            case SortableColumns.FINISH_DATE: return "finishDate";
            case SortableColumns.STATUS: return "status";
            default: return "";
        }
    };

    const setSurveyStatusType = (statusType) => setSelectedSurveyOrPollStatusType(statusType);

    const handleChangeTableType = (tableType) => {
        if (tableType === TableType.Surveys) {
            if (!canAccessSurveys) {
                return;
            }

            setShowPolls(false);
            getSurveys(selectedSurveyOrPollStatusType);
        } else {
            if (!canAccessPolls) {
                return;
            }

            setShowPolls(true);
            getPolls(selectedSurveyOrPollStatusType);
        }
    };

    const setShowPolls = (isShow) => setIsShowPolls(isShow);


    const { user } = props;
    const withSurveyStyles = content => (
        <>
            <SurveyGlobalStyles />
            {content}
        </>
    );
    const feature = pageFeatureContext;
    
    if (fetching > 0 && !initialized) {
        return withSurveyStyles(
            <PortalPageShell>
                <BusinessPortalHeader
                    title={feature.title}
                    description={`Create focused ${feature.singularLabel}s for ${pluralize(customNames.employees.toLowerCase())} or ${pluralize(customNames.clients.toLowerCase())}, then review real-time feedback and campaign performance.`}
                />
                <PortalLoadingState minHeight="600px">
                    <LottieLoadingIndicator />
                </PortalLoadingState>
            </PortalPageShell>
        );
    }

    if (isShowSurveyForm) {
        return withSurveyStyles(<SurveyForm showSurveyForm={showSurveyForm} user={user} getSurveys={getSurveys} surveyToEdit={surveyToEdit} selectedSurveyOrPollStatusType={selectedSurveyOrPollStatusType} />);
    }

    if (isShowCreatePollForm) {
        return withSurveyStyles(<PollsForm showPollForm={showCreatePollForm} user={user} setShowPolls={setShowPolls} getPolls={getPolls} pollToEdit={pollToEdit} setPollToEdit={setPollToEdit} setSelectedSurveyOrPollStatusType={setSelectedSurveyOrPollStatusType} />);
    }

    if (isShowInsights) {
        return withSurveyStyles(<SurveyInsightPage showInsights={showInsights} user={user} surveyUuid={surveyUuid} />);
    }

    if (isShowHowItWorks) {
        return withSurveyStyles(<HowItWorks showHowItWorks={showHowItWorks} />);
    }

    if (isShowPolls || (canAccessPolls && !canAccessSurveys)) {
        return withSurveyStyles(<PollsTable
            handleChangeTableType={handleChangeTableType}
            selectedTableType={isShowPolls || (canAccessPolls && !canAccessSurveys) ? TableType.Polls : TableType.Surveys}
            pollsArray={pollsArray}
            sortColumn={(key) => sortColumns(SortablePollColumns[key])}
            sortableColumns={sortableColumns}
            showPollForm={showCreatePollForm}
            showEditPollForm={showEditPollForm}
            showHowItWorks={showHowItWorks}
            surveyInsights={surveyInsights}
            fetchPoll={getPolls}
            fetching={fetching} user={user}
            setFetchingStatus={setFetchingStatus}
            setSurveyStatusType={setSurveyStatusType}
            selectedSurveyOrPollStatusType={selectedSurveyOrPollStatusType}
            canManagePolls={canAccessPolls}
            featureContext={pageFeatureContext}
            initialPollResultsUuid={calendarPollResultsUuid}
            onInitialPollResultsOpened={clearCalendarPollResultsUuid}
        />);
    }

    return withSurveyStyles(<SurveyTable
        handleChangeTableType={handleChangeTableType}
        selectedTableType={isShowPolls ? TableType.Polls : TableType.Surveys}
        surveyArray={surveyArray}
        sortableColumns={sortableColumns}
        sortColumn={(key) => sortColumns(SortableColumns[key])}
        showSurveyForm={showSurveyForm}
        editSurvey={showEditSurveyForm}
        showHowItWorks={showHowItWorks}
        surveyInsights={surveyInsights}
        fetchSurvey={getSurveys}
        fetching={fetching} user={user}
        setFetchingStatus={setFetchingStatus}
        setSurveyStatusType={setSurveyStatusType}
        selectedSurveyOrPollStatusType={selectedSurveyOrPollStatusType}
        canManageSurveys={canAccessSurveys}
        featureContext={pageFeatureContext}
    />);
};

export default withRouter(Survey);
